/**
 * Animated videos on the web: the page's path, whether the type is open to
 * this visitor (the server decides; ANIMATED_VIDEO_ENABLED), and the small
 * pure helpers the page uses.
 */
import {
  animatedVideoCreditCost,
  ANIMATED_VIDEO_CREDITS_PER_SECOND,
  ANIMATED_VIDEO_MAX_SECONDS,
  ANIMATED_VIDEO_MIN_SECONDS,
} from "@shared/animatedVideo";
import { trpc } from "@/lib/trpc";

export const ANIMATED_VIDEO_PATH = "/animated-music-video-maker";

/** The server's answer, with the shared constants until it arrives. */
export function useAnimatedVideoConfig() {
  const query = trpc.aiVideo.animatedConfig.useQuery(undefined, {
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });
  return {
    /** False until the server says otherwise, so nothing flashes for visitors it is closed to. */
    available: query.data?.available === true,
    loaded: query.isFetched,
    creditsPerSecond: query.data?.creditsPerSecond ?? ANIMATED_VIDEO_CREDITS_PER_SECOND,
    maxSeconds: query.data?.maxSeconds ?? ANIMATED_VIDEO_MAX_SECONDS,
    minSeconds: query.data?.minSeconds ?? ANIMATED_VIDEO_MIN_SECONDS,
  };
}

/** Whether the navigation should list the animated video page for this visitor. */
export function useAnimatedVideoAvailable(): boolean {
  return useAnimatedVideoConfig().available;
}

type AnimatedVideoLimits = { maxSeconds: number; minSeconds: number };

/** The part of the song a video is made from, in whole seconds of the song. */
export type AnimatedVideoRange = { start: number; end: number };

/** A new song starts whole, or as its first part when it runs over the limit. */
export function defaultAnimatedVideoRange(songSeconds: number, limits: AnimatedVideoLimits): AnimatedVideoRange {
  return { start: 0, end: Math.min(Math.max(0, Math.floor(songSeconds)), limits.maxSeconds) };
}

function clampSeconds(value: number, low: number, high: number): number {
  return Math.min(Math.max(value, low), Math.max(low, high));
}

/** The shortest and longest part of this song a video can be made from. */
function lengthBounds(songSeconds: number, limits: AnimatedVideoLimits) {
  const total = Math.max(0, Math.floor(songSeconds));
  // A song shorter than the shortest video has only itself to offer.
  return { total, min: Math.min(limits.minSeconds, total), max: Math.min(limits.maxSeconds, total) };
}

/**
 * Move one edge of the range to `to`, or slide the whole range ("both") so it
 * starts there. The range stays inside the song and between the length
 * limits: the edge being moved stops where a limit is.
 */
export function moveAnimatedVideoRange(
  range: AnimatedVideoRange,
  edge: "start" | "end" | "both",
  to: number,
  songSeconds: number,
  limits: AnimatedVideoLimits,
): AnimatedVideoRange {
  const { total, min, max } = lengthBounds(songSeconds, limits);
  const at = Math.round(to);
  if (edge === "both") {
    const length = clampSeconds(range.end - range.start, min, max);
    const start = clampSeconds(at, 0, total - length);
    return { start, end: start + length };
  }
  if (edge === "start") {
    return { start: clampSeconds(at, Math.max(0, range.end - max), range.end - min), end: range.end };
  }
  return { start: range.start, end: clampSeconds(at, range.start + min, Math.min(total, range.start + max)) };
}

/** A range `seconds` long from this one's start, pulled back when the song ends sooner. */
export function resizeAnimatedVideoRange(
  range: AnimatedVideoRange,
  seconds: number,
  songSeconds: number,
  limits: AnimatedVideoLimits,
): AnimatedVideoRange {
  const { total, min, max } = lengthBounds(songSeconds, limits);
  const length = clampSeconds(Math.round(seconds), min, max);
  const start = clampSeconds(range.start, 0, total - length);
  return { start, end: start + length };
}

export type AnimatedVideoPlan = {
  /** Seconds of video that will be made (and charged). Null until the song's length is known. */
  seconds: number | null;
  credits: number | null;
  /** The window aiVideo.create is sent; null when the video is the whole song. */
  part: { startTime: number; endTime: number } | null;
  tooShort: boolean;
};

/**
 * What the picked part of a song turns into: the same arithmetic as the
 * server's create mutation (whole seconds, 3 credits each). With no range,
 * the song is used whole, or its first part when it runs over the limit.
 */
export function planAnimatedVideo(
  songSeconds: number | null,
  limits: AnimatedVideoLimits,
  range?: AnimatedVideoRange | null,
): AnimatedVideoPlan {
  if (!songSeconds || !Number.isFinite(songSeconds) || songSeconds <= 0) {
    return { seconds: null, credits: null, part: null, tooShort: false };
  }
  const { start, end } = range ?? defaultAnimatedVideoRange(songSeconds, limits);
  // The whole song goes up untrimmed; the server then counts its full length.
  const whole = start <= 0 && end >= Math.floor(songSeconds);
  const seconds = Math.max(1, whole ? Math.floor(songSeconds) : end - start);
  return {
    seconds,
    credits: animatedVideoCreditCost(seconds),
    part: whole ? null : { startTime: start, endTime: end },
    tooShort: seconds < limits.minSeconds,
  };
}

/**
 * A title for the title card out of a file name: no extension, and no
 * underscores or hyphens standing in for spaces ("my-song_final.mp3"). A name
 * that already has spaces keeps its hyphens ("Semi-Charmed Life.mp3").
 */
export function titleFromFileName(name: string): string {
  const base = name.replace(/\.[^.]+$/, "").trim();
  const spaced = /\s/.test(base) ? base.replace(/_+/g, " ") : base.replace(/[_-]+/g, " ");
  return spaced.replace(/\s+/g, " ").trim();
}
